import {
  Component,
  EventEmitter,
  Input,
  OnChanges, OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TaskRequestSidenavService } from '../../../core/services/task-request-sidenav.service';
import { Subscription } from 'rxjs';
import { toExcelFormat } from '../../../helpers/format-position';
import { debounceTime } from 'rxjs/operators';
import { StockDataSourceService } from '../../../core/data-sources/stock-data-source.service';
import {
  ProductPickingDataSourceService
} from '../../../core/data-sources/product-picking-data-source.service';
import { ActionService, SetProductToPickingPositionAction } from '../../../core/services/action.service';
import { ActionsEnum, SendTasksTypeEnum } from '../../../core/data-sources/action-data-source.service';
import { SidenavDestinationComponent } from 'src/app/shared/sidenav-destination/sidenav-destination.component';

@Component({
  selector: 'app-add-product-on-product-picking-sidenav',
  templateUrl: './add-product-on-product-picking-sidenav.component.html',
  styleUrls: ['./add-product-on-product-picking-sidenav.component.scss']
})
export class AddProductOnProductPickingSidenavComponent implements OnInit, OnChanges, OnDestroy {

  @Input() data;
  @Input() drawer;
  @Output() changed = new EventEmitter();
  @ViewChild('destination', {static: true}) sidenavDestination: SidenavDestinationComponent;
  relocateForm: FormGroup;
  productOptions = [];
  palletsOptions = [];
  usersHighlighted = [];
  productInputSubscription: Subscription;
  resetInputSubscription: Subscription;
  isProductChosen = false;
  selectOperatorError = false;
  noPickingSectorError = false;
  noPalletsError = false;
  minRequiredError = false;
  maxRequiredError = false;
  destinationData = null;
  isMultiProduct = false;
  isSingleProduct = true;
  minMaxTooltip = 'O mínimo indica a quantidade mínima do produto para que o sistema emita uma nova tarefa de' +
    ' realocação ou ordem de compra. O máximo indica a quantidade máxima de produtos permitida na posição, bloqueando' +
    ' adições de novos produtos.';

  constructor(private sidenavService: TaskRequestSidenavService,
    private formBuilder: FormBuilder,
    private stockService: StockDataSourceService,
    private productPickingService: ProductPickingDataSourceService,
    private actionService: ActionService) {
    this.buildProductForm();
  }

  ngOnInit(): void {
    this.handleValueChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.resetForms();
  }

  ngOnDestroy(): void {
    this.productInputSubscription?.unsubscribe();
    this.resetInputSubscription?.unsubscribe();
  }

  buildProductForm() {
    this.relocateForm = this.formBuilder.group({
      productSku: ['', Validators.required],
      pallet: [[], Validators.required],
      quantity: [''],
      min: [null],
      max: [null],
    });
    this.applyMinMaxValidators();
  }

  handleValueChanges() {
    this.resetInputSubscription = this.relocateForm.controls.productSku.valueChanges.subscribe(() => {
      if (this.isProductChosen) {
        this.isProductChosen = false;
        this.relocateForm.controls.productSku.reset();
      }
    });
    this.productInputSubscription = this.relocateForm.controls.productSku.valueChanges.pipe(debounceTime(250))
      .subscribe(async (newValue) => {
        this.productOptions = await this.productPickingService.getProducts(newValue);
        if (newValue?.sku) {
          this.isProductChosen = true;
          await this.getPallets(newValue.sku);
        } else {
          this.palletsOptions = [];
        }
    });

    this.relocateForm.controls.min.valueChanges.subscribe((value) => {
      const max = this.relocateForm.controls.max;
      if (this.isSingleProduct) {
        if (value != null && value !== '') {
          if (max.value != null && max.value < value) {
            max.setValue(value);
          }
          max.setValidators([Validators.required, Validators.min(value)]);
        } else {
          max.setValidators([Validators.required, Validators.min(0)]);
        }
      } else {
        max.clearValidators();
      }
      max.updateValueAndValidity({ emitEvent: false });
    });
  }

  applyMinMaxValidators() {
    if (!this.relocateForm) return;
    const min = this.relocateForm.controls.min;
    const max = this.relocateForm.controls.max;
    if (this.isSingleProduct) {
      min.setValidators([Validators.required, Validators.min(0)]);
      const minVal = min.value != null && min.value !== '' ? min.value : 0;
      max.setValidators([Validators.required, Validators.min(minVal)]);
    } else {
      min.clearValidators();
      max.clearValidators();
    }
    min.updateValueAndValidity({ emitEvent: false });
    max.updateValueAndValidity({ emitEvent: false });
  }

  setErrors() {
    this.selectOperatorError = !this.usersHighlighted.length;
    if (this.isSingleProduct) {
      this.minRequiredError = this.relocateForm.controls.min.invalid;
      this.maxRequiredError = this.relocateForm.controls.max.invalid;
    } else {
      this.minRequiredError = false;
      this.maxRequiredError = false;
    }
  }

  displayedString(product) {
    if (product) {
      return product.name + ' - ' + product.sku;
    } else {
      return null;
    }
  }

  async getPallets(sku) {
    this.palletsOptions = await this.stockService.getPallets(sku);
    this.noPalletsError = this.palletsOptions.length === 0;
  }

  handlePosition(pallet) {
    return pallet.groundPosition?.alias ?? `--- (coordenada: ${toExcelFormat(pallet.x, pallet.y)})`;
  }

  handleQuantity(skuGroups) {
    let quantity = 0;
    for (const p of skuGroups) {
      quantity = (p.quantity * p.packSize) + quantity;
    }
    return quantity;
  }

  setUsers(users) {
    this.usersHighlighted = users;
  }

  resetForms() {
    this.sidenavDestination.reset();
    this.relocateForm.reset();
    this.usersHighlighted = [];
    this.productOptions = [];
    this.palletsOptions = [];
    this.noPickingSectorError = false;
    this.noPalletsError = false;
    this.selectOperatorError = false;
    this.minRequiredError = false;
    this.maxRequiredError = false;
  }

  async submit() {
    this.relocateForm.markAllAsTouched();
    this.setErrors();
    const { data: destination } = this.destinationData;
    const { x, y } = destination.address;
    const z = destination.level.z;
    if (this.isSingleProduct && this.relocateForm.valid && this.usersHighlighted.length) {
      const body: SetProductToPickingPositionAction = {
        type: ActionsEnum.SET_PRODUCT_TO_PICKING_POSITION,
        data: {
          sku: this.relocateForm.get('productSku').value.sku,
          groundPosition: { x, y, z },
          tagRfid: this.relocateForm.get('pallet').value.tagRfid,
          minQuantity: this.relocateForm.get('min').value,
          maxQuantity: this.relocateForm.get('max').value,
          userIds: this.usersHighlighted,
          showGreenScreen: this.destinationData.data.showGreenScreen
        }
      };
      await this.actionService.setProductToPickingPosition(body);
    } else if (!this.isSingleProduct && this.relocateForm.valid && this.usersHighlighted.length) {
      const params = {
        type: SendTasksTypeEnum.RELOCATE_POSITION,
        userIds: this.usersHighlighted,
        tagsRfid: [this.relocateForm.get('pallet').value.tagRfid],
        taskData: {
          destination: {
            x: destination.address.x,
            y: destination.address.y,
            z: destination.level.z
          },
          showGreenScreen: this.destinationData.data.showGreenScreen
        }
      };
      await this.actionService.bulkSendTask(params);
    } else {
      this.setErrors();
      return;
    }
    this.sidenavService.closeSidenav(true);
    this.drawer.close();
    this.resetForms();
  }

  async close() {
    this.resetForms();
    this.sidenavService.closeSidenav(false);
    this.drawer.close();
  }

  destinationDataChanged(event) {
    this.destinationData = event;
  }

  changePickingType(tab) {
    this.resetForms();
    if (tab.index === 0) {
      this.isSingleProduct = true;
      this.isMultiProduct = false;
    } else {
      this.isSingleProduct = false;
      this.isMultiProduct = true;
    }
    this.applyMinMaxValidators();
  }

}
